const formatTimeLabel = (iso) => {
  const d = new Date(iso)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

export const mapBmsHistoryToPoints = (history) => {
  if (!Array.isArray(history)) return []
  return history
    .map((snap) => {
      const packVoltage = Array.isArray(snap.cells) ? snap.cells.reduce((sum, mv) => sum + mv, 0) / 1000 : undefined
      return {
        time: formatTimeLabel(snap.ts),
        epochMs: new Date(snap.ts).getTime(),
        SoC: snap.soc_pct,
        soh: snap.soh_pct,
        'Pack Voltage': packVoltage,
        Current: snap.current_A,
        ...Object.fromEntries((snap.cells ?? []).map((mv, i) => [`Cell ${i + 1}`, mv])),
        ...Object.fromEntries((snap.ntc_C ?? []).map((c, i) => [`NTC ${i + 1}`, c])),
        afeTemp_C: snap.afeTemp_C,
        boardTemp_C: snap.boardTemp_C,
        mosfetTemp_C: snap.mosfetTemp_C,
        prechargeTemp_C: snap.prechargeTemp_C,
        balancingTemp_C: snap.balancingTemp_C,
        shuntTemp_C: snap.shuntTemp_C,
        chgCycles: snap.chgCycles,
        dischgCycles: snap.dischgCycles,
        balancingFlags: snap.balancingFlags,
        ahGained_mAh: snap.ahGained_mAh,
        ahSpent_mAh: snap.ahSpent_mAh,
        whSpent: snap.whSpent,
        fullCapacity_mAh: snap.fullCapacity_mAh,
        remainingCapacity_mAh: snap.remainingCapacity_mAh,
        powerMode: snap.powerMode === 1,
        batteryMode: snap.batteryMode === 1,
        chargeMosfet: snap.fetStatus?.chg_on,
        dischargeMosfet: snap.fetStatus?.dsg_on,
        prechargeMosfet: snap.fetStatus?.pchg_on,
        fwVersion: snap.fwVersion,
        protocolMajor: snap.protocolMajor,
        protocolMinor: snap.protocolMinor,
        mcuType: snap.mcuType,
        pchgFailReason: snap.pchgFailReason,
        faultExtra: snap.faultExtra,
        faultStatusRaw: snap.faultStatusRaw,
        cellVoltMax_mV: snap.cellVoltMax_mV,
        cellVoltMin_mV: snap.cellVoltMin_mV,
        tempMax_C: snap.tempMax_C,
        tempMin_C: snap.tempMin_C,
        ntcCount: snap.ntcCount,
        faults: snap.faults,
      }
    })
    .sort((a, b) => a.epochMs - b.epochMs)
}

const onOff = (value) => (value ? 'ON' : 'OFF')

// The BMS reports charge, voltage and energy in its native small units (mAh,
// mV, Wh); the table shows the human-scale ones (Ah, V, kWh), which is the same
// quantity divided by 1000. Rounding at 3 decimals is exactly the source
// resolution — one small unit — and keeps 3333 mV from rendering as
// 3.3330000000000002 V.
const perMille = (value) => (value != null ? Math.round(value) / 1000 : undefined)

const CELL_KEY_RE = /^Cell (\d+)$/
const NTC_KEY_RE = /^NTC (\d+)$/

// Cell/NTC counts vary by pack, so the indexed table columns are derived from
// the keys each point actually carries instead of a fixed 16/7 — a pack with
// more cells than that would otherwise have its extra cells silently dropped
// (the column headers come from the API and would render permanently blank).
const indexedColumns = (point, keyRe, outPrefix, transform = (v) => v) =>
  Object.fromEntries(
    Object.keys(point)
      .map((key) => keyRe.exec(key))
      .filter(Boolean)
      .map(([key, index]) => [`${outPrefix}${index}`, transform(point[key])]),
  )

// Values here are unit-free — the unit lives in the column header instead (see
// AllDataTable's UNIT_BY_KEY). Current keeps its explicit `+` sign, which reads
// as direction (charge vs discharge) rather than as a unit.
export const mapPointsToTableRows = (points) =>
  [...points]
    .sort((a, b) => b.epochMs - a.epochMs)
    .map((p) => ({
      timestamp: new Date(p.epochMs).toLocaleString(),
      soc_pct: p.SoC,
      soh_pct: p.soh,
      current_A: p.Current != null ? `${p.Current > 0 ? '+' : ''}${p.Current}` : undefined,
      afeTemp_C: p.afeTemp_C,
      boardTemp_C: p.boardTemp_C,
      mosfetTemp_C: p.mosfetTemp_C,
      prechargeTemp_C: p.prechargeTemp_C,
      balancingTemp_C: p.balancingTemp_C,
      shuntTemp_C: p.shuntTemp_C,
      tempMax_C: p.tempMax_C,
      tempMin_C: p.tempMin_C,
      chgCycles: p.chgCycles,
      dischgCycles: p.dischgCycles,
      balancingFlags: p.balancingFlags,
      // These keys keep their wire-unit suffixes (_mAh, _mV) because they have
      // to match the column keys the API hands back — the value under them is
      // the converted one, and the header states which unit that is.
      ahGained_mAh: perMille(p.ahGained_mAh),
      ahSpent_mAh: perMille(p.ahSpent_mAh),
      whSpent: perMille(p.whSpent),
      fullCapacity_mAh: perMille(p.fullCapacity_mAh),
      remainingCapacity_mAh: perMille(p.remainingCapacity_mAh),
      cellVoltMax_mV: perMille(p.cellVoltMax_mV),
      cellVoltMin_mV: perMille(p.cellVoltMin_mV),
      ntcCount: p.ntcCount,
      powerMode: p.powerMode != null ? onOff(p.powerMode) : undefined,
      batteryMode: p.batteryMode != null ? onOff(p.batteryMode) : undefined,
      'fetStatus.chg_on': p.chargeMosfet != null ? onOff(p.chargeMosfet) : undefined,
      'fetStatus.dsg_on': p.dischargeMosfet != null ? onOff(p.dischargeMosfet) : undefined,
      'fetStatus.pchg_on': p.prechargeMosfet != null ? onOff(p.prechargeMosfet) : undefined,
      fwVersion: p.fwVersion,
      protocolMajor: p.protocolMajor,
      protocolMinor: p.protocolMinor,
      mcuType: p.mcuType,
      pchgFailReason: p.pchgFailReason,
      faultExtra: p.faultExtra,
      faultStatusRaw: p.faultStatusRaw,
      faults: Array.isArray(p.faults) ? p.faults.join(', ') : undefined,
      ...indexedColumns(p, CELL_KEY_RE, 'cell', perMille),
      ...indexedColumns(p, NTC_KEY_RE, 'ntc'),
    }))
